import { FEED_REACH } from '../../shared/region';
import { ButtonLink } from '../components/Button';
import { EmptyBoard, Ridgeline } from '../components/illustrations';
import { useSession } from '../features/auth/session';
import { CONTACT, SiteFooter, SiteHeader } from '../features/landing/SiteChrome';
import styles from '../features/landing/Landing.module.css';

/** Why Roadrunner exists and who made it. Public, whether or not you're signed in. */
export function About() {
  const { session } = useSession();
  const action = session ? { label: 'Your board', href: '/' } : { label: 'Join', href: '/welcome' };

  return (
    <div className={styles.page}>
      <SiteHeader action={action} />

      <main id="main" className={`${styles.wrap} ${styles.prose}`}>
        <div className={styles.sign}>
          <h1 className={styles.headline}>Why Roadrunner exists.</h1>
          <p className={styles.lede}>A neighborhood board for people who actually live near each other.</p>
          <div className={styles.ridge}>
            <Ridgeline height={44} />
          </div>
        </div>

        <section className={styles.article} aria-label="The story">
          <p>
            My wife has used Nextdoor for years to keep up with our corner of Catalina: lost dogs, road work, and who to
            call when something breaks. Over time, though, Nextdoor kept widening what it counts as a neighborhood.
            Before long, her feed up here was full of posts from the city of Tucson, about 20 miles away. Those are good
            people, but they aren’t our neighbors.
          </p>
          <p>
            So I built Roadrunner to be truly local. Everyone confirms they live here before they can read or post, and
            your board only shows notes from people within {FEED_REACH} of home, newest first. If four neighbors posted
            today, you see four notes. The feed is never padded out with posts from across town.
          </p>

          <h2>What makes it different</h2>
          <ul>
            <li>
              <strong>Close to home.</strong> A few miles is enough for Catalina and SaddleBrooke to share a board,
              and far enough from the city that it still feels like our place.
            </li>
            <li>
              <strong>Verified neighbors.</strong> Your phone confirms your location once, from home. Neighbors see your
              name and your town, never your street.
            </li>
            <li>
              <strong>Newest first.</strong> No algorithm decides what you see or keeps you scrolling.
            </li>
          </ul>

          <h2>Where it’s open</h2>
          <p>
            Roadrunner is open to the northern Tucson suburbs: north of Ina Road, between I-10 and Kolb Road, and up to
            Oracle and San Manuel. That covers Catalina, SaddleBrooke, Oro Valley, Rancho Vistoso, Dove Mountain,
            Oracle, San Manuel and the neighborhoods in between.
          </p>

          <h2>Who’s behind it</h2>
          <p>
            I’m Nicholas De Leon, and I live in Catalina. Roadrunner is a small project I build and look after myself.
            If you have an idea, run into a problem, or want Roadrunner where you live, I’d love to hear from you by{' '}
            <a href={`mailto:${CONTACT.email}`}>email</a> or on{' '}
            <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer">
              LinkedIn
            </a>
            .
          </p>
        </section>

        {!session && (
          <section className={styles.closing} aria-labelledby="about-join">
            <div className={styles.closingArt}>
              <EmptyBoard />
            </div>
            <h2 id="about-join" className={styles.closingTitle}>
              Your board is waiting
            </h2>
            <ButtonLink to="/welcome" variant="primary">
              Join your neighbors
            </ButtonLink>
          </section>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
